import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined'
import styled, { css, keyframes } from 'styled-components'
import { Card } from 'sutra/components/Card/Card'
import { ChevronDown } from 'sutra/components/Icons'
import { Text } from 'sutra/components/Text/Text'
import { textSizeStyles } from 'sutra/components/Text/text-styles'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'
import { spacing } from 'sutra/tokens/spacing'

const BRAND_LINK_COLOR = '#45689B'

const TIMELINE_LINE_COLOR = '#e5e7eb'
const TIMELINE_DOT_COLOR = '#d1d5db'
const TIMELINE_DOT_SIZE = 10
const TIMELINE_COLUMN_WIDTH = 20

export const PanelCard = styled(Card)`
  flex: 1 1 480px;
  min-width: 480px;
  box-shadow: none;
  border: 1px solid #e9eaeb;
  padding: 24px 24px 40px 24px;
`

export const PanelHeader = styled.header`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${spacing[4]}px;
`

// styled(Text) drops `$size`, so each wrapper below restates its size in CSS;
// render with `forwardedAs`, since `as` would replace Text.
export const PanelTitle = styled(Text)`
  ${textSizeStyles.lg}
  font-weight: 600;
  color: ${brand.stillNight[800]};
`

export const RowList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
`

const clickableRowStyles = css`
  cursor: pointer;
  &:hover {
    background: ${gray[50]};
  }
`

export const Row = styled.li<{ $clickable?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: ${TIMELINE_COLUMN_WIDTH}px 1fr auto;
  gap: ${spacing[3]}px;
  align-items: center;
  min-height: 40px;
  padding: ${spacing[2]}px ${spacing[2]}px;
  border-radius: ${radii.sm}px;
  ${({ $clickable }) => ($clickable ? clickableRowStyles : '')}
`

export const TimelineCell = styled.span`
  position: relative;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${TIMELINE_COLUMN_WIDTH}px;

  /* Negative offsets bleed past Row's vertical padding so segments meet. */
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    width: 1px;
    background: ${TIMELINE_LINE_COLOR};
    transform: translateX(-50%);
  }
  &::before {
    top: -${spacing[2]}px;
    bottom: 50%;
  }
  &::after {
    top: 50%;
    bottom: -${spacing[2]}px;
  }

  ${Row}:first-child &::before {
    display: none;
  }
  ${Row}:last-child &::after {
    display: none;
  }
`

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(66, 128, 219, 0.45); }
  70% { box-shadow: 0 0 0 8px rgba(66, 128, 219, 0); }
  100% { box-shadow: 0 0 0 0 rgba(66, 128, 219, 0); }
`

export const TimelineDot = styled.span<{
  $variant: 'running' | 'failed' | 'found' | 'empty'
}>`
  position: relative;
  z-index: 1;
  width: ${TIMELINE_DOT_SIZE}px;
  height: ${TIMELINE_DOT_SIZE}px;
  border-radius: 9999px;
  background: ${({ $variant }) =>
    $variant === 'running'
      ? accents.reflectionBlue[500]
      : $variant === 'failed'
        ? '#f97066'
        : TIMELINE_DOT_COLOR};
  ${({ $variant }) =>
    $variant === 'running' &&
    css`
      animation: ${pulse} 1.6s cubic-bezier(0.66, 0, 0, 1) infinite;
    `}
`

export const RowWarningIcon = styled(ErrorOutlineOutlined)`
  font-size: 20px !important;
  color: #f97066;
`

// $stretch: ::after covers the positioned ancestor (Row) so the whole row
// is clickable while the anchor stays scoped to just this text.
export const LinkText = styled(Text)<{ $stretch?: boolean }>`
  ${textSizeStyles.sm}
  font-weight: 600;
  color: ${BRAND_LINK_COLOR};
  white-space: nowrap;
  text-decoration: none;
  ${({ $stretch }) =>
    $stretch &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
      }
      &:focus-visible {
        outline: none;
      }
      &:focus-visible::after {
        outline: 2px solid ${BRAND_LINK_COLOR};
        outline-offset: 2px;
      }
    `}
`

export const RowTimestamp = styled(Text)`
  ${textSizeStyles.sm}
  white-space: nowrap;
`

export const RowTextStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

// 12/16 — one step below Text's `sm` (14/20) so the subtitle reads as
// secondary next to the row title.
export const RowSubtitle = styled(Text)`
  font-size: 12px;
  line-height: 16px;
`

// Link-styled button rendered inline inside RowSubtitle. Font inherits from
// the subtitle; brand color signals clickability.
export const SubtitleEditButton = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  color: ${BRAND_LINK_COLOR};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${BRAND_LINK_COLOR};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

export const SkeletonWrap = styled.div`
  padding: ${spacing[2]}px 0;
`

export const EmptyState = styled(Text)`
  ${textSizeStyles.sm}
  padding: ${spacing[8]}px 0;
  text-align: center;
`

export const ExpandLink = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  padding: ${spacing[4]}px 0 0 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]}px;
  color: ${BRAND_LINK_COLOR};
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${BRAND_LINK_COLOR};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

export const ExpandChevron = styled(ChevronDown)<{ $isExpanded: boolean }>`
  transition: transform 150ms ease;
  transform: rotate(${({ $isExpanded }) => ($isExpanded ? 180 : 0)}deg);
`
